import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Box, Stack, Typography, Link, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip } from '@mui/material';
import CalendarView from '../components/CalendarView';
import SnackbarAlert from '../components/SnackbarAlert';
import BookingModal from '../components/BookingModal';
import ChargerModal from '../components/ChargerModal';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  fetchMyBookings,
  bookSlotSelf,
  fetchNextAvailableDates,
  deleteBooking,
  reportProblem,
  deleteReport,
  fetchAvailableSpots,
  createMySlotRelease,
  fetchMySlotReleases,
  deleteMySlotRelease,
  fetchAssignedSlotBookedDates,
  createAssignmentReport,
  fetchMyAssignmentReports
} from '../api/bookings';
import { updateElectricCarSelf } from '../api/users';
import dayjs from 'dayjs';
import BookingsActions from '../components/BookingsActions';
import MyBookingsList from '../components/MyBookingsList';
import ConfirmDialog from '../components/ConfirmDialog';
import ReportDialog from '../components/ReportDialog';
import AssignmentReportDialog from '../components/AssignmentReportDialog';
import TodayBookingsList from '../components/TodayBookingsList';
import { Delete as DeleteIcon } from '@mui/icons-material';

function HomePage() {
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [availableDates, setAvailableDates] = useState({ next_day: null, second_day: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, bookingId: null });
  const [reportDialog, setReportDialog] = useState({
    open: false,
    bookingId: null,
    plateNumber: '',
    comments: ''
  });
  const [reportContent, setReportContent] = useState(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [bookingModal, setBookingModal] = useState({ open: false, date: null });
  const [chargerModal, setChargerModal] = useState({ open: false, slot: null });
  const [availableSlotsData, setAvailableSlotsData] = useState({});
  const { t } = useTranslation();
  const { userInfo, setUserInfo } = useContext(AuthContext);
  const today = dayjs().format('YYYY-MM-DD');
  const [reportIdToRemove, setReportIdToRemove] = useState(null);
  const [syntheticBookings, setSyntheticBookings] = useState([]);
  const [releases, setReleases] = useState([]);
  const [releaseDialog, setReleaseDialog] = useState({ open: false, start: '', end: '' });
  const [assignedBookedDates, setAssignedBookedDates] = useState([]);
  const [releaseConfirmDialog, setReleaseConfirmDialog] = useState({ open: false, action: '', releaseId: null, start: '', end: '' });
  const [assignmentReportDialog, setAssignmentReportDialog] = useState({
    open: false,
    date: null,
    plateNumber: '',
    comments: ''
  });
  const [assignmentReports, setAssignmentReports] = useState([]);

  // Use refs to access current values without causing re-renders
  const myBookingsRef = useRef(myBookings);
  const userInfoRef = useRef(userInfo);
  const isComputingRef = useRef(false);
  const lastComputeTime = useRef(0);

  // Update refs when state changes
  useEffect(() => {
    myBookingsRef.current = myBookings;
  }, [myBookings]);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const loadMyBookings = useCallback(async () => {
    try {
      setLoadingBookings(true);
      const data = await fetchMyBookings();
      const bookings = data.my_bookings || [];
      setMyBookings(bookings);
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  const loadAvailableDates = useCallback(async () => {
    try {
      const data = await fetchNextAvailableDates();
      setAvailableDates(data);
    } catch {}
  }, []);

  const loadReleases = useCallback(async () => {
    if (!userInfo?.assigned_slot) { setReleases([]); setAssignedBookedDates([]); return; }
    try {
      const year = dayjs().year();
      const [releasesData, bookedData] = await Promise.all([
        fetchMySlotReleases(year),
        fetchAssignedSlotBookedDates(year)
      ]);
      setReleases(releasesData.releases || []);
      setAssignedBookedDates(bookedData.booked_dates || []);
    } catch {}
  }, [userInfo]);

  const loadAssignmentReports = useCallback(async () => {
    if (!userInfo?.assigned_slot) { setAssignmentReports([]); return; }
    try {
      const data = await fetchMyAssignmentReports();
      setAssignmentReports(data.assignment_reports || []);
    } catch (err) {
      console.error('Error loading assignment reports:', err);
    }
  }, [userInfo?.assigned_slot]);

  const handleCancelRelease = async (releaseId) => {
    const release = releases.find(r => r.id === releaseId);
    if (release) {
      setReleaseConfirmDialog({
        open: true,
        action: 'cancel',
        releaseId: releaseId,
        start: release.start_date,
        end: release.end_date
      });
    }
  };

  const handleConfirmCancelRelease = async () => {
    try {
      const response = await deleteMySlotRelease(releaseConfirmDialog.releaseId);
      await loadReleases();
      await refreshBookings();
      
      // Show success message
      setSnackbar({ open: true, message: t('bookings.release_cancelled_success'), severity: 'success' });
      
              // Show alert about booked dates if there are any
        if (response && response.booked_dates && response.booked_dates.length > 0) {
          const bookedDatesFormatted = response.booked_dates.map(date => dayjs(date).format('DD/MM/YYYY')).join(', ');
          const alertMessage = t('bookings.assigned_slot_booked_alert', { 
            dates: bookedDatesFormatted,
            slot: userInfo?.assigned_slot?.slot_number 
          });
          
          // Show a warning snackbar about the booked dates
          setTimeout(() => {
            setSnackbar({ 
              open: true, 
              message: alertMessage, 
              severity: 'warning',
              autoHideDuration: 8000 // Show for 8 seconds to give user time to read
            });
          }, 1000); // Delay to show after success message
        }
      
      setReleaseConfirmDialog({ open: false, action: '', releaseId: null, start: '', end: '' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleCalendarDayClick = async ({ date }) => {
    const formattedDate = dayjs(date).format('YYYY-MM-DD');

    if (isDateBooked(formattedDate)) {
      setSnackbar({ open: true, message: t('bookings.already_booked_for_date'), severity: 'warning' });
      return;
    }

    if (dayjs(formattedDate).isBefore(dayjs(), 'day')) {
      setSnackbar({ open: true, message: t('bookings.cannot_book_past_date'), severity: 'error' });
      return;
    }

    // Fetch available slots data to check for charger information
    try {
      const slotsData = await fetchAvailableSpots(formattedDate);
      setAvailableSlotsData(prev => ({ ...prev, [formattedDate]: slotsData.available_spots || [] }));
    } catch (error) {
      console.error('Error fetching available slots:', error);
    }

    setBookingModal({ open: true, date: formattedDate });
  };

  const handleBookSlot = async (date, slot_id = null) => {
    try {
      await bookSlotSelf({ date, slot_id });
      await refreshBookings();

      // Check if the booked slot has a charger and show modal if it does
      const slotsForDate = availableSlotsData[date] || [];
      const bookedSlot = slotsForDate.find(slot => slot.id === slot_id);

      if (bookedSlot && bookedSlot.has_charger) {
        setChargerModal({
          open: true,
          slot: {
            slotNumber: bookedSlot.slot_number,
            address: bookedSlot.address
          }
        });
      } else {
        setSnackbar({ open: true, message: t('bookings.booking_created_successfully'), severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleBookingModalConfirm = (date, slotId) => {
    handleBookSlot(date, slotId);
    setBookingModal({ open: false, date: null });
  };

  const handleBookingModalClose = () => setBookingModal({ open: false, date: null });

  const handleChargerModalClose = () => {
    setChargerModal({ open: false, slot: null });
    setSnackbar({ open: true, message: t('bookings.booking_created_successfully'), severity: 'success' });
  };

  const handleSlotsDataUpdate = useCallback((date, slots) => {
    setAvailableSlotsData(prev => ({ ...prev, [date]: slots }));
  }, []);

  const handleDeleteBooking = async (bookingId) => {
    try {
      if (typeof bookingId === 'string' && bookingId.startsWith('assigned-')) {
        const date = bookingId.split('assigned-')[1];
        await createMySlotRelease({ start_date: date });
        await loadReleases();
      } else {
        await deleteBooking(bookingId);
      }
      await refreshBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const openConfirmDialog = (bookingId) => setConfirmDialog({ open: true, bookingId });
  const closeConfirmDialog = () => setConfirmDialog({ open: false, bookingId: null });
  const confirmDeleteBooking = async () => {
    if (confirmDialog.bookingId) {
      await handleDeleteBooking(confirmDialog.bookingId);
      closeConfirmDialog();
    }
  };

  const openReportDialog = (bookingId) => setReportDialog({ open: true, bookingId, plateNumber: '', comments: '' });
  const closeReportDialog = () => setReportDialog({ open: false, bookingId: null, plateNumber: '', comments: '' });

  const handleReportProblem = async () => {
    const { bookingId, plateNumber, comments } = reportDialog;
    try {
      await reportProblem(bookingId, plateNumber, comments);
      setSnackbar({ open: true, message: 'Problem reported successfully.', severity: 'success' });
      refreshBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      closeReportDialog();
    }
  };

  const openAssignmentReportDialog = (date) => setAssignmentReportDialog({ open: true, date, plateNumber: '', comments: '' });
  const closeAssignmentReportDialog = () => setAssignmentReportDialog({ open: false, date: null, plateNumber: '', comments: '' });

  const handleCreateAssignmentReport = async () => {
    const { date, plateNumber, comments } = assignmentReportDialog;
    try {
      await createAssignmentReport(date, plateNumber, comments);
      setSnackbar({ open: true, message: t('assignment_report.created_successfully'), severity: 'success' });
      await loadAssignmentReports();
      refreshBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      closeAssignmentReportDialog();
    }
  };

  const handleToggleElectricCar = async () => {
    try {
      const response = await updateElectricCarSelf();
      setUserInfo((prev) => ({ ...prev, has_electric_car: !prev.has_electric_car }));
      setSnackbar({ open: true, message: response.message, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const isDateBooked = (date) => {
    const combined = [...myBookings, ...syntheticBookings];
    const hasBooking = combined.some((booking) => booking.date === date && !booking.report);
    const hasAssignmentReport = assignmentReports.some((report) => report.date === date);
    return hasBooking && !hasAssignmentReport;
  };
  
  const refreshBookings = async () => {
    try {
      const data = await fetchMyBookings();
      setMyBookings(data.my_bookings || []);
    } catch {}
  };

  const computeSyntheticBookings = useCallback(async () => {
    // Prevent multiple simultaneous calls and rate limit to max once per second
    const now = Date.now();
    if (isComputingRef.current || (now - lastComputeTime.current) < 1000) {
      return;
    }
    
    isComputingRef.current = true;
    lastComputeTime.current = now;
    
    try {
      const results = [];
      const addedDates = new Set();
      const currentUserInfo = userInfoRef.current;
      const currentBookings = myBookingsRef.current;
      
      if (!currentUserInfo?.assigned_slot) {
        setSyntheticBookings([]);
        return;
      }

      const datesToCheck = [today, availableDates.next_day, availableDates.second_day].filter(Boolean);
      
      if (datesToCheck.length === 0) {
        setSyntheticBookings([]);
        return;
      }
      
      // Batch all API calls to avoid multiple sequential calls
      const apiCalls = datesToCheck.map(date => 
        fetchAvailableSpots(date).catch(() => ({ blocked_by_assigned_slot: false }))
      );
      
      const responses = await Promise.all(apiCalls);
      
      datesToCheck.forEach((date, index) => {
        const data = responses[index];
        if (data.blocked_by_assigned_slot && !currentBookings.find(b => b.date === date) && !addedDates.has(date)) {
          results.push({ 
            id: `assigned-${date}`, 
            date: date, 
            slot_id: currentUserInfo.assigned_slot.id, 
            slot_number: currentUserInfo.assigned_slot.slot_number, 
            address: currentUserInfo.assigned_slot.address, 
            slot_has_charger: currentUserInfo.assigned_slot.has_charger, 
            is_guest: false, 
            report: null 
          });
          addedDates.add(date);
        }
      });

      setSyntheticBookings(results);
    } catch (error) {
      console.error('Error computing synthetic bookings:', error);
      setSyntheticBookings([]);
    } finally {
      isComputingRef.current = false;
    }
  }, [today, availableDates.next_day, availableDates.second_day]);

  useEffect(() => {
    loadMyBookings();
    loadAvailableDates();
    loadReleases();
    loadAssignmentReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMyBookings, loadAvailableDates, loadReleases]);

  useEffect(() => { loadReleases(); }, [userInfo, loadReleases]);
  
  // Recompute synthetic bookings when essential dependencies change
  useEffect(() => { 
    computeSyntheticBookings(); 
  }, [availableDates.next_day, availableDates.second_day, userInfo?.assigned_slot?.id, computeSyntheticBookings]);

  // Recompute synthetic bookings when myBookings changes, but with a debounce to prevent rapid calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      computeSyntheticBookings();
    }, 200);
    
    return () => clearTimeout(timeoutId);
  }, [myBookings.length, computeSyntheticBookings]); // Only depend on length to avoid infinite loops

  const mergedBookings = [...myBookings, ...syntheticBookings];

  const handleOpenReleaseDialog = () => {
    setReleaseDialog({ open: true, start: today, end: today });
  };
  const handleCloseReleaseDialog = () => setReleaseDialog({ open: false, start: '', end: '' });
  const handleCreateRelease = async () => {
    setReleaseConfirmDialog({
      open: true,
      action: 'create',
      releaseId: null,
      start: releaseDialog.start,
      end: releaseDialog.end
    });
  };

  const handleConfirmCreateRelease = async () => {
    try {
      await createMySlotRelease({ start_date: releaseConfirmDialog.start, end_date: releaseConfirmDialog.end });
      setSnackbar({ open: true, message: t('bookings.release_created_success'), severity: 'success' });
      setReleaseConfirmDialog({ open: false, action: '', releaseId: null, start: '', end: '' });
      handleCloseReleaseDialog();
      await loadReleases();
      await refreshBookings();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const releaseDataSource = releases.flatMap(r => {
    const start = dayjs(r.start_date);
    const end = dayjs(r.end_date);
    const days = [];
    for (let d = start; d.isBefore(end) || d.isSame(end, 'day'); d = d.add(1, 'day')) {
      const iso = d.format('YYYY-MM-DD');
      const isBooked = assignedBookedDates.includes(iso);
      days.push({ startDate: d.toDate(), endDate: d.toDate(), color: isBooked ? '#4da3ff' : '#cfe8ff', name: isBooked ? 'Released (booked)' : 'Released' });
    }
    return days;
  });
  console.debug('Release ranges:', releases, 'Release day entries:', releaseDataSource.length);

  const bookingDataSource = mergedBookings.map((booking) => ({
    startDate: new Date(booking.date),
    endDate: new Date(booking.date),
    color: 'lightgreen',
    name: 'Your Booking'
  }));

  const byDate = {};
  const dateKey = (d) => dayjs(d.startDate).format('YYYY-MM-DD');
  releaseDataSource.forEach(entry => { byDate[dateKey(entry)] = entry; });
  bookingDataSource.forEach(entry => { byDate[dateKey(entry)] = entry; });
  const combinedDataSource = Object.values(byDate);

  const upcomingReleases = releases.filter(r => dayjs(r.end_date).isSame(dayjs(), 'day') || dayjs(r.end_date).isAfter(dayjs(), 'day'));
  
  // Function to check if a release can be cancelled
  const canCancelRelease = (release) => {
    // If it's a single day release and that day is booked by someone else, can't cancel
    if (release.start_date === release.end_date && assignedBookedDates.includes(release.start_date)) {
      return false;
    }
    
    // If it's a multi-day release, check if all days are booked
    const start = dayjs(release.start_date);
    const end = dayjs(release.end_date);
    let allDaysBooked = true;
    
    for (let d = start; d.isBefore(end) || d.isSame(end, 'day'); d = d.add(1, 'day')) {
      const iso = d.format('YYYY-MM-DD');
      if (!assignedBookedDates.includes(iso)) {
        allDaysBooked = false;
        break;
      }
    }
    
    return !allDaysBooked;
  };

  const viewReportDetails = (report) => {
    setReportIdToRemove(report.id);
    setReportContent(
      <>
        <Typography><strong>Number Plate:</strong> {report.number_plate}</Typography>
        <Typography><strong>Comments:</strong> {report.comments}</Typography>
      </>
    );
    setIsReportOpen(true);
  };

  const removeReport = async (reportId) => {
    try {
      await deleteReport(reportId);
      setSnackbar({ open: true, message: 'Report removed successfully.', severity: 'success' });
      refreshBookings();
      setIsReportOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove report.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Box sx={{ width: { xs: '100%', md: '25%' } }}>
          {userInfo?.assigned_slot && (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('bookings.assigned_slot_message', { slot: userInfo.assigned_slot.slot_number })}
                </Typography>
              </Box>
              <Button variant="outlined" fullWidth sx={{ mb: 2 }} onClick={handleOpenReleaseDialog}>
                {t('bookings.new_release')}
              </Button>
              {upcomingReleases.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('bookings.releases')}
                  </Typography>
                  <Stack spacing={1}>
                    {upcomingReleases.map((r) => (
                      <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}
                          </Typography>
                          {!canCancelRelease(r) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              ({r.start_date === r.end_date ? t('bookings.cannot_cancel_booked_single') : t('bookings.cannot_cancel_booked')})
                            </Typography>
                          )}
                        </Box>
                        {canCancelRelease(r) && (
                          <Tooltip title={t('actions.cancel')}>
                            <IconButton size="small" onClick={() => handleCancelRelease(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
          <BookingsActions
            t={t}
            availableDates={availableDates}
            handleBookSlot={handleBookSlot}
            isDateBooked={isDateBooked}
            userInfo={userInfo}
            handleToggleElectricCar={handleToggleElectricCar}
            loadingBookings={loadingBookings}
            myBookings={mergedBookings}
            onSlotsDataUpdate={handleSlotsDataUpdate}
          />
          <MyBookingsList
            t={t}
            myBookings={mergedBookings}
            loadingBookings={loadingBookings}
            availableDates={availableDates}
            today={today}
            openConfirmDialog={openConfirmDialog}
            openReportDialog={openReportDialog}
            viewReportDetails={viewReportDetails}
            openAssignmentReportDialog={openAssignmentReportDialog}
            assignmentReports={assignmentReports}
          />
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" align="center" sx={{ mt: 2, color: 'text.secondary' }}>
              <Link href="https://confluence.bluetab.net/pages/viewpage.action?pageId=209649854" target="_blank" rel="noopener" sx={{ ml: 1, fontWeight: 'bold' }}>
                {t('general.parking_policies_link')}
              </Link>
            </Typography>
            <TodayBookingsList />
          </Box>
        </Box>
        <Box sx={{ width: { xs: '100%', md: '75%' } }}>
          <CalendarView
            onDayClick={handleCalendarDayClick}
            setSnackbar={setSnackbar}
            dataSource={combinedDataSource}
            showUserPastBookings={true}
          />
        </Box>
      </Stack>
      <BookingModal open={bookingModal.open} date={bookingModal.date} onClose={handleBookingModalClose} onConfirm={handleBookingModalConfirm} />
      {chargerModal.slot && (
        <ChargerModal
          open={chargerModal.open}
          onClose={handleChargerModalClose}
          slotNumber={chargerModal.slot.slotNumber}
          address={chargerModal.slot.address}
        />
      )}
      <ConfirmDialog open={confirmDialog.open} onClose={closeConfirmDialog} onConfirm={confirmDeleteBooking} title={t('actions.confirm_cancelation')} children={t('bookings.confirm_deletion_question')} t={t} />
      <ReportDialog open={reportDialog.open} plateNumber={reportDialog.plateNumber} comments={reportDialog.comments} onClose={closeReportDialog} onSubmit={handleReportProblem} setReportDialog={setReportDialog} t={t} />
      <AssignmentReportDialog 
        open={assignmentReportDialog.open} 
        date={assignmentReportDialog.date} 
        plateNumber={assignmentReportDialog.plateNumber} 
        comments={assignmentReportDialog.comments} 
        onClose={closeAssignmentReportDialog} 
        onSubmit={handleCreateAssignmentReport} 
        setAssignmentReportDialog={setAssignmentReportDialog} 
        t={t} 
      />
      <Dialog open={releaseDialog.open} onClose={handleCloseReleaseDialog}>
        <DialogTitle>{t('bookings.new_release')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <TextField label={t('general.date')} type="date" value={releaseDialog.start} onChange={(e) => setReleaseDialog((p) => ({ ...p, start: e.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField label={t('general.date')} type="date" value={releaseDialog.end} onChange={(e) => setReleaseDialog((p) => ({ ...p, end: e.target.value }))} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReleaseDialog}>{t('actions.cancel')}</Button>
          <Button onClick={handleCreateRelease} variant="contained">{t('actions.confirm')}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        onConfirm={() => removeReport(reportIdToRemove)}
        title={t('bookings.report_info')}
        children={reportContent}
        t={t}
      />
      <ConfirmDialog
        open={releaseConfirmDialog.open}
        onClose={() => setReleaseConfirmDialog({ open: false, action: '', releaseId: null, start: '', end: '' })}
        onConfirm={releaseConfirmDialog.action === 'create' ? handleConfirmCreateRelease : handleConfirmCancelRelease}
        title={releaseConfirmDialog.action === 'create' ? t('bookings.confirm_create_release') : t('bookings.confirm_cancel_release')}
        children={releaseConfirmDialog.action === 'create' 
          ? t('bookings.confirm_create_release_question', { start: releaseConfirmDialog.start, end: releaseConfirmDialog.end })
          : t('bookings.confirm_cancel_release_question', { start: releaseConfirmDialog.start, end: releaseConfirmDialog.end })
        }
        t={t}
      />
      <SnackbarAlert open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} />
    </Box>
  );
}

export default HomePage;
